import React from 'react'

import { LocalShipping, RequestQuote } from '@styled-icons/material'

import styled from '@emotion/styled'

import {
  LoadingOutlined,
  BuildOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  ShoppingOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { Card, Tooltip, Typography, Spin, Space, Button } from 'antd'
import { ButtonProps } from 'antd/lib/button'

import { Box } from 'common/components/boxes'
import { ContractorIcon } from 'common/components/ContractorIcon'
import { useTheme } from 'common/hooks/use-theme'

const ItemCount = ({ helper, icon, count }) => {
  return (
    <Tooltip title={helper}>
      <Box display="inline-flex" alignItems="center">
        {icon}
        <Typography.Text type="secondary" style={{ marginLeft: 4 }}>
          {count}
        </Typography.Text>
      </Box>
    </Tooltip>
  )
}

const CardStyled = styled(Card)`
  height: 146px;
  overflow: auto;
  display: flex;
  flex-direction: column;

  .ant-card-body {
    flex-grow: 1;
  }
`

const Logo = ({ src }) => {
  const theme = useTheme()

  if (src) {
    return <img src={src} style={{ objectFit: 'cover', marginLeft: 16, maxHeight: 60, maxWidth: 120 }} />
  }

  return <ContractorIcon style={{ color: theme.colors.primary, marginLeft: 16, fontSize: 48 }} />
}

type CustomerCardProps = {
  title: string
  logoUrl?: string
  isWatching: boolean
  onToggleWatcher: () => void
  quotedCount?: number
  orderedCount?: number
  shippedCount?: number
  deliveredCount?: number
  isLoading?: boolean
  onClickSettings: ButtonProps['onClick']
}

export const CustomerCard = (props: CustomerCardProps) => {
  const {
    title,
    logoUrl,
    isWatching,
    onToggleWatcher,
    quotedCount = 0,
    orderedCount = 0,
    shippedCount = 0,
    deliveredCount = 0,
    isLoading = false,
    onClickSettings,
  } = props

  const handleToggleWatcher = (event) => {
    event.stopPropagation()
    onToggleWatcher()
  }

  return (
    <CardStyled
      hoverable
      actions={[
        <ItemCount key="1" helper="Quoted" count={quotedCount} icon={<RequestQuote size="20" />} />,
        <ItemCount key="2" helper="Ordered" count={orderedCount} icon={<ShoppingOutlined />} />,
        <ItemCount
          key="3"
          helper="Shipped & Partially Shipped"
          count={shippedCount}
          icon={<LocalShipping size="20" />}
        />,
        <ItemCount key="4" helper="Delivered" count={deliveredCount} icon={<BuildOutlined />} />,
        isWatching ? (
          <Spin key="5" spinning={isLoading} indicator={<LoadingOutlined spin />}>
            <Tooltip title={`Stop watching ${title}`}>
              <EyeTwoTone onClick={handleToggleWatcher} />
            </Tooltip>
          </Spin>
        ) : (
          <Spin key="6" spinning={isLoading} indicator={<LoadingOutlined spin />}>
            <Tooltip title={`Watch ${title}`}>
              <EyeInvisibleOutlined onClick={handleToggleWatcher} />
            </Tooltip>
          </Spin>
        ),
      ]}
      bodyStyle={{ padding: '0' }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" height="100%" p={16}>
        <Space align="center">
          <Typography.Title level={5} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
          <Button icon={<SettingOutlined />} size="small" onClick={onClickSettings} />
        </Space>
        <Logo src={logoUrl} />
      </Box>
    </CardStyled>
  )
}
